import React from "react";
import { Pagination } from "@pd/pd-ui-kit";
import { format } from "date-fns";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";

import Wrapper from "~/shared/ui/Wrapper";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { TCatalog_identifier } from "~/model/Instances/TCatalog";
import { CATALOG_EDITOR_OPTIONS } from "~/pages/Editor/EditorDocumentSection/utils/options";
import {
  useGetCatalogListQuery,
  useGetCatalogQuery,
} from "~/schemas/generated/graphql";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2, useTableSort } from "~/shared/ui/TableV2";

import {
  CATALOGS_SORTABLE_COLUMNS,
  getCatalogsColumns,
} from "./model/tableAdapter";
import { extend_CATALOG_EDITOR_OPTIONS } from "./utils/options";
import { handleRowClick_Document } from "./utils/utils";

import s from "./s.less";

type Params = {
  id: string;
};

const Catalogs = () => {
  const history = useHistory();
  const { id } = useParams<Params>();
  const isExternalCatalogView = !id;

  const { t } = useTranslation();
  const {
    page,
    count,
    sortField,
    sortType: sortTypeRaw,
  } = useGetLocationData();
  const setData = useSetLocationData();

  // Бэкенд DocumentGetter.getSections не принимает order/orderBy,
  // поэтому сортировка клиентская, а в URL хранится только её состояние
  const { sortType, onSortChange } = useTableSort({
    sortField,
    sortType: sortTypeRaw,
    sortableColumns: CATALOGS_SORTABLE_COLUMNS,
    setData,
  });

  const tableSortField =
    sortField && CATALOGS_SORTABLE_COLUMNS.includes(sortField)
      ? sortField
      : undefined;

  const { handleCount, handlePage } = usePaginationParams();

  const { data: catalogData, loading: catalogLoading } = useGetCatalogQuery({
    variables: { id },
    skip: !id,
    fetchPolicy: "no-cache",
  });

  const { data, loading } = useGetCatalogListQuery({
    variables: { parentId: id || undefined, page, count },
    fetchPolicy: "no-cache",
  });

  const selectedCatalog = get(catalogData, "DocumentGetter.getOneSection");

  const catalogList = get(data, "DocumentGetter.getSections.items", []).map(
    (el) => ({
      ...el,
      createdAtRaw: el.createdAt ? new Date(el.createdAt).getTime() : 0,
      createdAt: format(new Date(el.createdAt), "dd.MM.yyyy"),
    }),
  );
  const catalogListTotal = get(data, "DocumentGetter.getSections.total", 0);

  const title = isExternalCatalogView
    ? t("documents.title")
    : `${t("documents.catalog")} ${
        catalogLoading ? "..." : (selectedCatalog?.name ?? "")
      }`;

  const columns = getCatalogsColumns(
    t,
    isExternalCatalogView
      ? CATALOG_EDITOR_OPTIONS
      : extend_CATALOG_EDITOR_OPTIONS(CATALOG_EDITOR_OPTIONS),
    tableSortField,
    sortType,
  );

  const goBack = () =>
    history.push(
      selectedCatalog?.parentId
        ? `/documents/sub_catalogs/${selectedCatalog.parentId}`
        : "/documents/",
    );

  const handleRowClick = (record) => {
    const canClick =
      (record as TCatalog_identifier)?.childCount > 0 || !isExternalCatalogView;
    if (canClick) {
      handleRowClick_Document(history, !isExternalCatalogView)(record);
    }
  };

  return (
    <Wrapper.Content
      title={title}
      txtBtn={!isExternalCatalogView ? t("ui.back") : undefined}
      goBackHandler={!isExternalCatalogView ? goBack : undefined}
    >
      <TableV2
        wrapperClassName={s.tableWrapper}
        columns={columns}
        dataSource={catalogList}
        rowKey="id"
        loading={loading}
        sortableColumns={CATALOGS_SORTABLE_COLUMNS}
        sortField={tableSortField}
        sortType={tableSortField ? sortType : undefined}
        onSortChange={onSortChange}
        onRow={(record) => {
          const canClick =
            (record as TCatalog_identifier)?.childCount > 0 ||
            !isExternalCatalogView;
          return {
            onClick: canClick ? () => handleRowClick(record) : undefined,
            style: canClick ? { cursor: "pointer" } : undefined,
          };
        }}
      />
      <Pagination
        count={count}
        page={page + 1}
        totalCount={catalogListTotal}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />
    </Wrapper.Content>
  );
};

export default Catalogs;
